import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { OrganizationWithRelations } from "@/types/organization.types";
import { OrganizationHeader } from "@/components/organization/OrganizationHeader";
import { DeleteOrganizationDialog } from "@/components/organization/DeleteOrganizationDialog";
import { MergeOrganizationDialog } from "@/components/organization/MergeOrganizationDialog";
import { DetailsTab } from "@/components/organization/tabs/DetailsTab";
import { HierarchyTab } from "@/components/organization/tabs/HierarchyTab";
import { ContactsTab } from "@/components/organization/tabs/ContactsTab";
import { OpportunitiesTab } from "@/components/organization/tabs/OpportunitiesTab";
import { ActivitiesTab } from "@/components/organization/tabs/ActivitiesTab";
import { AccountPlanningTab } from "@/components/organization/tabs/AccountPlanningTab";
import { OrganizationErrorCard } from "@/components/organization/OrganizationErrorCard";
import { OrganizationLoading } from "@/components/organization/OrganizationLoading";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useToast } from "@/hooks/use-toast";
import { ContractsTab } from "@/components/organization/tabs/ContractsTab";
import { ProjectsTab } from "@/components/organization/tabs/ProjectsTab";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertTriangle, Clock } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { fetchOrganizationWithDetails } from "@/services/organization/organizationService";

// Enhanced organization details with optimized loading
export default function EnhancedOrganizationDetails() {
  const { id } = useParams<{ id: string }>();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { deleteOrganization, mergeOrganizations } = useOrganizations();

  // Single optimized query using React Query
  const {
    data: organization,
    isLoading,
    error,
    refetch,
    isStale,
    dataUpdatedAt
  } = useQuery({
    queryKey: ['organization-details', id],
    queryFn: () => fetchOrganizationWithDetails(id!),
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    onError: (error: any) => {
      toast({
        title: "Error loading organization",
        description: error.message || "Failed to load organization details",
        variant: "destructive",
      });
    }
  });

  // Enhanced error handling
  const handleDelete = async (organizationId: string) => {
    try {
      await deleteOrganization(organizationId);
      toast({
        title: "Organization deleted",
        description: "The organization has been successfully deleted.",
      });
      navigate("/organizations");
    } catch (error: any) {
      toast({
        title: "Failed to delete organization",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleMerge = async (sourceId: string, targetId: string) => {
    try {
      await mergeOrganizations({ sourceId, targetId });
      toast({
        title: "Organizations merged",
        description: "The organizations have been successfully merged.",
      });
      navigate("/organizations");
    } catch (error: any) {
      toast({
        title: "Failed to merge organizations",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Calculate data freshness
  const getDataFreshnessMessage = () => {
    if (!dataUpdatedAt) return null;
    const now = new Date();
    const lastUpdate = new Date(dataUpdatedAt);
    const diffMinutes = Math.floor((now.getTime() - lastUpdate.getTime()) / 60000);
    
    if (diffMinutes < 1) return "Just updated";
    if (diffMinutes < 60) return `Updated ${diffMinutes} minutes ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    return `Updated ${diffHours} hours ago`;
  };

  // Loading state with progressive disclosure
  if (isLoading) {
    return <OrganizationLoading />;
  }

  // Enhanced error state with recovery options
  if (error || !organization || !id) {
    return (
      <div className="space-y-6">
        <OrganizationErrorCard error={error?.message} />
        <div className="flex justify-center">
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Calculate counts for tab badges
  const contactCount = organization.contacts?.length || 0;
  const opportunityCount = organization.opportunities?.length || 0;
  const activitiesCount = organization.activities?.length || 0;
  const contractsCount = 0; // Will be calculated by ContractsTab
  const projectsCount = 0; // Will be calculated by ProjectsTab

  return (
    <div className="space-y-6">
      {/* Data staleness warning */}
      {isStale && (
        <Alert>
          <Clock className="h-4 w-4" />
          <AlertDescription>
            This data may be outdated. {getDataFreshnessMessage()}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => refetch()}
              className="ml-2"
            >
              <RefreshCw className="mr-1 h-3 w-3" />
              Refresh
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Organization Header */}
      <OrganizationHeader 
        organization={organization} 
        id={id}
        onDelete={() => setIsDeleteDialogOpen(true)}
        onMerge={() => setIsMergeDialogOpen(true)}
        onRefresh={() => refetch()}
        lastUpdated={getDataFreshnessMessage()}
      />

      {/* Enhanced Tabs with badges and validation */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:grid-cols-8">
          <TabsTrigger value="details" className="text-xs sm:text-sm">
            Details
          </TabsTrigger>
          <TabsTrigger value="hierarchy" className="text-xs sm:text-sm">
            Hierarchy
          </TabsTrigger>
          <TabsTrigger value="contacts" className="text-xs sm:text-sm relative">
            Contacts 
            {contactCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 w-auto text-xs">
                {contactCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="opportunities" className="text-xs sm:text-sm relative">
            Opportunities
            {opportunityCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 w-auto text-xs">
                {opportunityCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="activities" className="text-xs sm:text-sm relative">
            Activities
            {activitiesCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 w-auto text-xs">
                {activitiesCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="account-planning" className="text-xs sm:text-sm">
            Planning
          </TabsTrigger>
          <TabsTrigger value="contracts" className="text-xs sm:text-sm">
            Contracts
          </TabsTrigger>
          <TabsTrigger value="projects" className="text-xs sm:text-sm">
            Projects
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="details" className="space-y-6">
            <DetailsTab 
              organization={organization} 
              organizationId={id} 
              contacts={organization.contacts || []}
              onDataChange={() => refetch()}
            />
          </TabsContent>

          <TabsContent value="hierarchy" className="space-y-6">
            <HierarchyTab 
              organizationId={id}
              onDataChange={() => refetch()}
            />
          </TabsContent>

          <TabsContent value="contacts" className="space-y-6">
            <ContactsTab 
              organization={organization} 
              id={id}
              onDataChange={() => refetch()}
            />
          </TabsContent>

          <TabsContent value="opportunities" className="space-y-6">
            <OpportunitiesTab 
              organization={organization} 
              id={id}
              onDataChange={() => refetch()}
            />
          </TabsContent>

          <TabsContent value="activities" className="space-y-6">
            <ActivitiesTab 
              organization={organization} 
              id={id}
              onDataChange={() => refetch()}
            />
          </TabsContent>
          
          <TabsContent value="account-planning" className="space-y-6">
            <AccountPlanningTab 
              organizationId={id}
              organizationName={organization.name}
              onDataChange={() => refetch()}
            />
          </TabsContent>

          <TabsContent value="contracts" className="space-y-6">
            <ContractsTab 
              organizationId={id}
              onDataChange={() => refetch()}
            />
          </TabsContent>

          <TabsContent value="projects" className="space-y-6">
            <ProjectsTab 
              organizationId={id}
              onDataChange={() => refetch()}
            />
          </TabsContent>
        </div>
      </Tabs>

      {/* Confirmation Dialogs */}
      {isDeleteDialogOpen && (
        <DeleteOrganizationDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          organization={organization}
          onDelete={handleDelete}
        />
      )}

      {isMergeDialogOpen && (
        <MergeOrganizationDialog
          isOpen={isMergeDialogOpen}
          onClose={() => setIsMergeDialogOpen(false)}
          organization={organization}
          onMerge={handleMerge}
        />
      )}
    </div>
  );
}